import {Component as AppComponent} from "../enum/Component";
import {UploadedFile} from "../model/UploadedFile";
import {ObjectID} from "bson";
import {UploadedFilePreview} from "../model/UploadedFilePreview";
import axios from "axios";
import {PaidStatus} from "../model/enums/PaidStatus";
import {PaymentDetails} from "../model/PaymentDetails";
import { normalizeEmptyFields } from "./normalizeEmptyFields";

export class SubmissionUtils{

    public static makePaymentCompleteRequest = async (submissionId: string, appVersion: string,
                                                      transactionId: string) => {
        const pathArray = window.location.pathname.split('/').filter(Boolean);
        let uri = "/api/webapp/paymentCompleted/"+pathArray[1];
        let data = {
            submissionId: submissionId,
            appVersion: appVersion,
            transactionId: transactionId
        };
        try {
            return await axios.post(uri, data);
        } catch(e) {
            return null;
        }
    };

    public static makePaymentFailedRequest = async (submissionId: string, appVersion: string,
                                                      transactionId: string) => {
        const pathArray = window.location.pathname.split('/').filter(Boolean);
        let uri = "/api/webapp/paymentFailed/"+pathArray[1];
        let data = {
            submissionId: submissionId,
            appVersion: appVersion,
            transactionId: transactionId
        };
        try {
            return await axios.post(uri, data);
        } catch(e) {
            return null;
        }
    };

    public static async submitAppData(app: any, appData: any, token: string, isStripePayment: boolean,
                                      isPaypalPayment: boolean, transactionId?: string,
                                      amountToPay?: string): Promise<any>{
        const normalizedAppData = normalizeEmptyFields(appData)
        const pathArray = window.location.pathname.split('/').filter(Boolean);
        let uri = "/api/webapp/submit/app/"+pathArray[1];
        if(pathArray[2] !== undefined && pathArray[2] !== ""){
            uri = `${uri}/${pathArray[2]}`;
        }
        let paymentDetails: PaymentDetails|undefined;

        if (app.payment !== undefined) {
            paymentDetails = new PaymentDetails(
                (isStripePayment) ?
                    (amountToPay === "0.00" ?
                        PaidStatus.NoCharge :
                        PaidStatus.Pending) :
                    (isPaypalPayment ?
                        PaidStatus.Paid :
                        ((!amountToPay || amountToPay === "0.00") ?
                            PaidStatus.NoCharge :
                            PaidStatus.Unpaid)),
                transactionId);
        }

        const formData = await SubmissionUtils.populateFormData(token, normalizedAppData, app.version, paymentDetails);
        return await axios.post(uri, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }

    public static async saveAppData(app: any, appData: any, token: string, pageIndex: number): Promise<any>{
        const normalizedAppData = normalizeEmptyFields(appData)
        const pathArray = window.location.pathname.split('/').filter(Boolean);
        let uri = "/api/webapp/save/app/"+pathArray[1];
        if(pathArray[2] !== undefined && pathArray[2] !== ""){
            uri = `${uri}/${pathArray[2]}`;
        }
        const formData = await SubmissionUtils.populateFormData(token, normalizedAppData, app.version, undefined,
            pageIndex);
        return await axios.post(uri, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }

    public static async savedAppDataEmail(email: string, subject: string, draftSubmissionId: string): Promise<any>{
        const pathArray = window.location.pathname.split('/').filter(Boolean);
        let uri = "/api/webapp/saved/app/email/"+pathArray[1];
        const data = {
            email: email,
            subject: subject,
            draftSubmissionId: draftSubmissionId
        }
        return await axios.post(uri, data);
    }

    public static async populateFormData(token: any, appData: any, appVersion: any, paymentDetails?: PaymentDetails,
                                         pageIndex?: number) {
        const formData = new FormData();
        const normalizedAppData = normalizeEmptyFields(appData);

        const blobURLtoBlob = async (blobURL: string): Promise<Blob> => {
            const response = await fetch(blobURL);
            return await response.blob();
        };

        // Collect all componentInfo first, then append them before files
        const componentInfos: Array<{ pageId: string; componentId: string; componentType: string; fileIds: string[] }> = [];

        for (const key in normalizedAppData) {
            for (const key1 in normalizedAppData[key]) {
                for (const key2 in normalizedAppData[key][key1]) {
                    let appDatumElementElement = normalizedAppData[key][key1][key2];

                    if (appDatumElementElement !== "" && appDatumElementElement !== null && key2 === AppComponent.FileUpload) {
                        const uploadedFiles: Array<UploadedFile> = [];
                        const fileIds: string[] = [];

                        for (const key3 in appDatumElementElement) {
                            const fileWrapper = appDatumElementElement[key3];
                            let objectID = new ObjectID();

                            let fileBlob: Blob;

                            try {
                                if (fileWrapper instanceof Blob) {
                                    fileBlob = fileWrapper;
                                }
                                else if (fileWrapper.preview?.url?.startsWith('blob:')) {
                                    fileBlob = await blobURLtoBlob(fileWrapper.preview.url);
                                }
                                else if (fileWrapper.preview?.url?.startsWith('data:')) {
                                    const arr = fileWrapper.preview.url.split(',');
                                    const mime = arr[0].match(/:(.*?);/)?.[1] || '';
                                    const bstr = atob(arr[1]);
                                    let n = bstr.length;
                                    const u8arr = new Uint8Array(n);
                                    while (n--) {
                                        u8arr[n] = bstr.charCodeAt(n);
                                    }
                                    fileBlob = new Blob([u8arr], { type: mime });
                                }
                                else {
                                    throw new Error(`Cannot find file data for ${fileWrapper.name}`);
                                }

                                // Store for later appending - DON'T append yet
                                const isImageDataUrl = fileWrapper.preview?.url?.startsWith('data:');
                                const uploadedFilePreview = new UploadedFilePreview(
                                    isImageDataUrl ? (fileWrapper.preview?.url || '') : '',
                                    isImageDataUrl ? (fileWrapper.preview?.type || '') : ''
                                );

                                let uploadedFile = new UploadedFile(
                                    objectID,
                                    fileWrapper.name,
                                    fileWrapper.sizeReadable,
                                    fileWrapper.size,
                                    fileWrapper.type,
                                    fileWrapper.extension,
                                    uploadedFilePreview
                                );

                                uploadedFiles.push(uploadedFile);
                                fileIds.push(objectID.toString());

                                // Store file data to append later (after componentInfo)
                                (uploadedFile as any)._blob = fileBlob;
                                (uploadedFile as any)._objectID = objectID;

                            } catch (error) {
                                throw new Error(`Cannot convert file ${fileWrapper.name} to uploadable format`);
                            }
                        }

                        // Collect componentInfo for this component
                        if (fileIds.length > 0) {
                            componentInfos.push({
                                pageId: key,
                                componentId: key1,
                                componentType: key2,
                                fileIds: fileIds
                            });
                        }

                        normalizedAppData[key][key1][key2] = uploadedFiles;
                    }
                }
            }
        }

        // IMPORTANT: Append componentInfo fields FIRST
        componentInfos.forEach(info => {
            formData.append('componentInfo', JSON.stringify(info));
        });

        // NOW append the actual files
        for (const key in normalizedAppData) {
            for (const key1 in normalizedAppData[key]) {
                for (const key2 in normalizedAppData[key][key1]) {
                    let appDatumElementElement = normalizedAppData[key][key1][key2];

                    if (appDatumElementElement !== "" && appDatumElementElement !== null && key2 === AppComponent.FileUpload) {
                        for (const uploadedFile of appDatumElementElement) {
                            const fileBlob = (uploadedFile as any)._blob;
                            const objectID = (uploadedFile as any)._objectID;

                            if (fileBlob && objectID) {
                                formData.append(`${objectID}`, fileBlob, uploadedFile.name);
                            }

                            // Clean up temporary properties
                            delete (uploadedFile as any)._blob;
                            delete (uploadedFile as any)._objectID;
                        }
                    }
                }
            }
        }

        // Then append other form data
        formData.append('data', JSON.stringify(normalizedAppData));
        formData.append('token', token);
        formData.append('appVersion', appVersion);
        if(paymentDetails !== undefined){
            formData.append('paymentDetails', JSON.stringify(paymentDetails));
        }
        if(pageIndex !== undefined){
            formData.append('pageIndex', pageIndex.toString());
        }

        return formData;
    }

}
